import React, { useContext, useState, useEffect } from "react";
import { McDrawer } from "@maersk-global/mds-react-wrapper/components-core/mc-drawer";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McSelect } from "@maersk-global/mds-react-wrapper/components-core/mc-select";
import { McOption } from "@maersk-global/mds-react-wrapper/components-core/mc-option";
import { McIcon } from "@maersk-global/mds-react-wrapper";
import DynamicInputSupportGroup from "./DynamicInputSupportGroup";
import { TableContext } from "../../context";
import { toast } from 'react-toastify';
import axios from "axios";

// Maersk font family
const maerskFontFamily = "Maersk Text, -apple-system, BlinkMacSystemFont, Microsoft JhengHei, Microsoft Yahei, 微软雅黑, STXihei, 华文细黑, sans-serif";

/**
 * SetGroupDrawer component for configuring approval and support groups
 * 
 * @param {boolean} isOpen - Whether the drawer is open
 * @param {Function} onClose - Function to close the drawer
 * @param {string} configureType - Type of group being configured ('approval_group' or 'support_group')
 * @param {string} appId - Application ID
 * @param {Array} appName - Array of application instance names
 * @param {Object} groupData - Current group data object with structure: { change_control: {displayValue, value, previousvalue}, support_group: {displayValue, value, previousvalue} }
 * @param {Function} onGroupDataChange - Function to handle group data changes
 * @param {Function} onSave - Function to handle save action (called after successful API save)
 * @returns {JSX.Element} The rendered drawer component
 */
const SetGroupDrawer = ({ 
  isOpen, 
  onClose, 
  configureType, 
  appId, 
  appName, 
  groupData, 
  onGroupDataChange, 
  onSave 
}) => {
  const { fetchWithTimeout } = useContext(TableContext);
  const [isSaving, setIsSaving] = useState(false);
  const [scopeOption, setScopeOption] = useState(''); // 'all_prod' or 'selective'
  const [selectedInstances, setSelectedInstances] = useState([]);

  // Reset state when drawer opens/closes
  useEffect(() => {
    if (!isOpen) {
      setScopeOption('');
      setSelectedInstances([]);
    }
  }, [isOpen]);

  // API call to save group configuration
  const saveGroupConfiguration = async () => {
    try {
      setIsSaving(true);
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      // Determine group value based on configureType
      const groupValue = configureType === 'approval_group' 
        ? groupData?.change_control?.displayValue 
        : groupData?.support_group?.displayValue;

      // Validate required data before proceeding
      if (!groupValue) {
        throw new Error(`Please select a ${configureType === 'approval_group' ? 'approval' : 'support'} group`);
      }

      if (!scopeOption) {
        throw new Error("Please select a scope option");
      }

      if (scopeOption === 'selective' && (!selectedInstances || selectedInstances.length === 0)) {
        throw new Error("Please select an application instance");
      }

      let endpoint, payload;

      if (scopeOption === 'all_prod') {
        // Use the new "all" endpoint
        endpoint = configureType === 'approval_group' ? 'set_approval_group_all' : 'set_support_group_all';
        payload = {
          application_id: appId,
          application_instances: Array.isArray(appName) ? appName : [appName],
          [configureType]: groupValue
        };
      } else {
        // Use the existing selective endpoint with selected instance
        endpoint = configureType === 'approval_group' ? 'set_approval_group' : 'set_support_group';
        payload = {
          application_name: selectedInstances[0], // Use the first selected instance
          [configureType]: groupValue
        };
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/${endpoint}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        
        // Handle different response formats based on scope
        if (scopeOption === 'all_prod') {
          // Handle batch update response for "all instances" endpoints
          if (result.status === "completed") {
            const summary = result.summary || {};
            const successful = summary.successful || 0;
            const failed = summary.failed || 0;
            const total = summary.total_requested || 0;
            
            if (successful > 0 && failed === 0) {
              // All updates successful
              toast.success(`${configureType === 'approval_group' ? 'Approval' : 'Support'} group updated successfully for all instances`, {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light"
              });
              onSave(); // Call the original onSave handler
              onClose(); // Close the drawer
            } else if (successful > 0 && failed > 0) {
              // Partial success
              toast.warning(`Partially completed: ${successful} successful, ${failed} failed out of ${total} instances`, {
                position: "top-center",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light"
              });
              onSave(); // Call the original onSave handler
              onClose(); // Close the drawer
            } else {
              // All failed
              throw new Error(`All updates failed`);
            }
          } else {
            console.error("Unexpected batch response format:", result);
            throw new Error("Failed to update group configuration");
          }
        } else {
          // Handle single instance response for selective endpoints
          if (result.status === "success" && result.status_code === 200) {
            toast.success(`${configureType === 'approval_group' ? 'Approval' : 'Support'} group updated successfully`, {
              position: "top-center",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light"
            });
            onSave(); // Call the original onSave handler
            onClose(); // Close the drawer
          } else {
            console.error("Unexpected response format:", result);
            throw new Error(result.message || "Failed to update group configuration");
          }
        }
      } else {
        console.error(`Failed to save ${configureType} - HTTP status:`, response.status);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error(`Error saving ${configureType}:`, error);
      toast.error(`Failed to save ${configureType === 'approval_group' ? 'approval' : 'support'} group configuration`, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Determine if save should be enabled based on whether a valid selection has been made
  const isSaveDisabled = () => {
    if (isSaving) return true; // Disable while saving
    
    // Must have scope selection
    if (!scopeOption) return true;
    
    // Must have group selection
    const hasGroupSelection = configureType === 'approval_group' 
      ? groupData?.change_control?.value && groupData.change_control.value !== ''
      : groupData?.support_group?.value && groupData.support_group.value !== '';
    
    if (!hasGroupSelection) return true;
    
    // If selective, must have instance selection
    if (scopeOption === 'selective' && selectedInstances.length === 0) return true;
    
    return false;
  };

  return (
    <McDrawer
      open={isOpen}
      closed={onClose}
    >
      <span slot="heading" style={{ color: "#0073ab", fontFamily: maerskFontFamily }}>
        Configure {configureType === 'approval_group' ? 'Approval Group' : 'Support Group'}
      </span>
      
      <div>
        {/* Application Info */}
        <div style={{ marginBottom: "24px", padding: "16px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
          <div style={{ marginBottom: "12px", fontFamily: maerskFontFamily }}>
            <strong>Application ID:</strong> {appId}
          </div>
          <div style={{ marginBottom: "12px", fontFamily: maerskFontFamily }}>
            <strong>Application Instance(s):</strong> {Array.isArray(appName) ? appName.join(', ') : appName}
          </div>
        </div>

        {/* Scope Selection */}
        <div style={{ marginBottom: "24px" }}>
          <p style={{ 
            display: "flex", 
            alignItems: "center", 
            marginBottom: "12px",
            fontWeight: "600",
            fontSize: "14px",
            fontFamily: maerskFontFamily
          }}>
            Set {configureType === 'approval_group' ? 'approval' : 'support'} group for:
            <span style={{ color: "red", marginLeft: "3px" }}>*</span>
          </p>
          <McSelect
            label=""
            placeholder="Choose scope"
            value={scopeOption}
            optionselected={(e) => {
              setScopeOption(e.detail.value);
              setSelectedInstances([]); // Reset instance selection when scope changes
            }}
            fit="medium"
            style={{ width: '100%' }}
          >
            <McOption value="all_prod">All Production Instances</McOption>
            <McOption value="selective">Selective Instances</McOption>
          </McSelect>
        </div>

        {/* Instance Selection (only for selective scope) */}
        {scopeOption === 'selective' && (
          <div style={{ marginBottom: "24px" }}>
            <p style={{ 
              display: "flex", 
              alignItems: "center", 
              marginBottom: "12px",
              fontWeight: "600",
              fontSize: "14px",
              fontFamily: maerskFontFamily
            }}>
              Select Instance:
              <span style={{ color: "red", marginLeft: "3px" }}>*</span>
            </p>
            <McSelect
              label=""
              placeholder="Choose application instance"
              value={selectedInstances[0] || ''}
              optionselected={(e) => {
                setSelectedInstances([e.detail.value]);
              }}
              fit="medium"
              style={{ width: '100%' }}
            >
              {Array.isArray(appName) ? appName.map((instanceName, index) => (
                <McOption key={index} value={instanceName}>
                  {instanceName}
                </McOption>
              )) : (
                <McOption value={appName}>{appName}</McOption>
              )}
            </McSelect>
          </div>
        )}

        {/* Group Configuration (only show if scope is selected) */}
        {scopeOption && (
          <>
            {configureType === 'approval_group' && (
              <div style={{ marginBottom: "24px" }}>
                <div style={{ marginBottom: "16px" }}>
                  <p style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    marginBottom: "8px",
                    fontWeight: "600",
                    fontSize: "14px",
                    fontFamily: maerskFontFamily
                  }}>
                    Approval Group
                    <span style={{ color: "red", marginLeft: "3px" }}>*</span>
                  </p>
                  <DynamicInputSupportGroup
                    group_type="approval_group"
                    type="suggestions"
                    name="change_control"
                    value={groupData}
                    originaldata={groupData}
                    disabled={false}
                    style={{
                      borderRadius: "8px",
                      border: "1px solid #ccc",
                      padding: "8px",
                      width: "100%",
                      backgroundColor: "white",
                      fontSize: "14px"
                    }}
                    onChange={onGroupDataChange}
                  />
                </div>
              </div>
            )}

            {configureType === 'support_group' && (
              <div style={{ marginBottom: "24px" }}>
                <div style={{ marginBottom: "16px" }}>
                  <p style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    marginBottom: "8px",
                    fontWeight: "600",
                    fontSize: "14px",
                    fontFamily: maerskFontFamily
                  }}>
                    Support Group
                    <span style={{ color: "red", marginLeft: "3px" }}>*</span>
                  </p>
                  <DynamicInputSupportGroup
                    group_type="support_group"
                    type="suggestions"
                    name="support_group"
                    value={groupData}
                    originaldata={groupData}
                    disabled={false}
                    style={{
                      borderRadius: "8px",
                      border: "1px solid #ccc",
                      padding: "8px",
                      width: "100%",
                      backgroundColor: "white",
                      fontSize: "14px"
                    }}
                    onChange={onGroupDataChange}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ServiceNow Links Section */}
      <div>
        <div style={{ 
          padding: "16px", 
          backgroundColor: "#f0f8ff", 
          borderRadius: "8px",
          border: "1px solid #e3f2fd"
        }}>
          <p style={{ 
            marginBottom: "12px", 
            fontWeight: "600", 
            fontSize: "14px",
            color: "#333",
            marginTop: 0,
            fontFamily: maerskFontFamily
          }}>
            Don't have an existing {configureType === 'approval_group' ? 'approval' : 'support'} group?
          </p>
          
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <a 
              href="https://maersk.service-now.com/end_user_portal?id=sc_cat_item&sys_id=e0e0fd24dbb9dcd4dfb0de0cd39619b5&table=sc_cat_item"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#0073ab",
                textDecoration: "none",
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: maerskFontFamily
              }}
              onMouseOver={(e) => e.target.style.textDecoration = "underline"}
              onMouseOut={(e) => e.target.style.textDecoration = "none"}
            >
              Create a New ServiceNow Group
            </a>
            
            <a 
              href="https://maersk.service-now.com/end_user_portal?id=sc_cat_item&sys_id=16a00c7bdba110d4dfb0de0cd396196f&table=sc_cat_item"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#0073ab",
                textDecoration: "none",
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: maerskFontFamily
              }}
              onMouseOver={(e) => e.target.style.textDecoration = "underline"}
              onMouseOut={(e) => e.target.style.textDecoration = "none"}
            >
              Modify a ServiceNow Group
            </a>
            
            <a 
              href="https://maersk.service-now.com/end_user_portal?id=sc_cat_item&sys_id=559b38251b00bbc0e39cdc27bd4bcb32&table=sc_cat_item"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#0073ab",
                textDecoration: "none",
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: maerskFontFamily
              }}
              onMouseOver={(e) => e.target.style.textDecoration = "underline"}
              onMouseOut={(e) => e.target.style.textDecoration = "none"}
            >
              Request ServiceNow Support/Approval Group Access
            </a>
          </div>
        </div>
      </div>

      <div slot="footer" style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
        <McButton 
          variant="outlined" 
          onClick={onClose}
        >
          Cancel
        </McButton>
        <McButton 
          variant="filled" 
          onClick={saveGroupConfiguration}
          disabled={isSaveDisabled()}
          loading={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </McButton>
      </div>
    </McDrawer>
  );
};

export default SetGroupDrawer;

